import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  BedDouble,
  ChevronRight,
  CircleDot,
  Clock,
  GraduationCap,
  Layers,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  PersonStanding,
  Plus,
  Sparkles,
  User,
  Zap,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/beard-transplant-mumbai";
const HAIR_TRANSPLANT_PATH = "/hair-transplant-mumbai";

const BEARD_TRANSPLANT_FAQS = [
  {
    question: "Is beard transplant permanent?",
    answer:
      "Yes. The transplanted follicles are taken from the scalp donor area where they are genetically resistant to the factors causing facial hair absence. Once established in the beard zone, these follicles grow permanently as beard hair. They can be shaved, trimmed, and styled without affecting their permanence.",
  },
  {
    question: "Will transplanted beard hair look natural?",
    answer:
      "Yes, when the implantation angle and direction are correctly matched to the natural beard growth pattern. The technical challenge in beard transplant is angle control. Hair implanted at the wrong angle grows away from the face rather than lying flat and immediately reads as unnatural. Dr. Jain maps the implantation angles across the beard zone before surgery to match the natural growth direction in each area.",
  },
  {
    question: "When can I shave after beard transplant?",
    answer:
      "No shaving for the first 10 to 14 days to allow the transplanted grafts to anchor securely. After this period, gentle shaving can resume. Normal shaving frequency is restored from the end of the first month onward without affecting the permanent grafts.",
  },
  {
    question: "How long does beard transplant take?",
    answer:
      "4 to 8 hours depending on the number of grafts and the areas being treated. The procedure is performed in a single session under local anesthesia. Patients go home the same day.",
  },
  {
    question: "Is beard transplant painful?",
    answer:
      "The procedure is performed under local anesthesia. The anesthesia injections to the donor area and the beard zone produce brief discomfort that resolves quickly. The extraction and implantation phases are painless. Mild soreness and swelling for 2 to 3 days after the procedure are managed with standard medication.",
  },
  {
    question: "How many sessions are needed for beard transplant?",
    answer:
      "Most patients achieve their desired result in a single session. A second session may be needed in patients who want maximum density above what one session provides, or in patients with scar tissue in the recipient area where graft survival is slightly lower than in unscarred tissue. Dr. Jain advises on the likely number of sessions during consultation based on the graft requirement and recipient area characteristics.",
  },
  {
    question: "Will the scalp donor area look thin after beard transplant?",
    answer:
      "No, provided the extraction is distributed evenly across the donor zone rather than concentrated in one spot. FUE extraction spreads the harvest across the full donor area, maintaining uniform density that is not visible once hair returns to normal length. The total number of grafts required for beard transplant is generally much lower than for full scalp restoration, making donor depletion extremely unlikely.",
  },
  {
    question: "Can beard transplant correct acne scars?",
    answer:
      "In many cases, yes. Transplant into acne-scarred skin is appropriate when the scar tissue has adequate blood supply to support graft survival. Heavily fibrotic scar tissue with poor vascularity has lower graft survival rates. Dr. Jain assesses the scar tissue quality during consultation to determine whether transplant is appropriate and what survival rate is realistic in the scarred zone.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalProcedure",
      name: "Beard transplant",
      description:
        "Minimally invasive surgical relocation of scalp follicles to the beard zone using FUE and DHI for permanent facial hair restoration; design and angles individualized per patient.",
      procedureType: "SurgicalProcedure",
      bodyLocation: [
        { "@type": "AnatomicalStructure", name: "Face" },
        { "@type": "AnatomicalStructure", name: "Beard" },
      ],
      url: PAGE_URL,
      medicalSpecialty: "https://schema.org/PlasticSurgery",
    },
    {
      "@type": "FAQPage",
      mainEntity: BEARD_TRANSPLANT_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function BeardTransplantJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Non-Surgical", href: "/non-surgical-treatments-in-mumbai" },
    { name: "Beard Transplant", href: "/beard-transplant-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Non-Surgical · Hair</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">Beard Transplant</h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Permanent Beard Restoration Using FUE &amp; DHI by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Beard Transplant" icon="🧔" size="hero"  imageSrc={HERO.beardTransplant} imageAlt="Beard transplant"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">What is a Beard Transplant?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              A beard transplant is a minimally invasive procedure where hair follicles are extracted from the scalp donor area, typically the back and sides of the head, and implanted into the beard zone to create permanent facial hair. The transplanted follicles grow as beard hair, can be shaved, trimmed, and styled normally, and produce results that are indistinguishable from natural beard growth when the technique is executed correctly.
            </p>
            <p>
              Many men have sparse, patchy, or absent beard growth due to genetics, hormonal factors, or scarring from acne, injury, or previous surgery. Beard growth oils, supplements, and topical treatments do not create new follicles in areas where none exist. They work only on follicles that are already present and active. Where follicles are absent or inactive, transplantation is the only approach that produces real, permanent hair growth.
            </p>
            <p>
              Dr. Vicky Jain performs beard transplant using FUE and DHI techniques. The technique selected depends on the graft count required, the precision needed in the implantation zone, and whether the patient wants to avoid shaving the recipient area during the procedure. The beard design is planned individually based on the patient&apos;s facial structure, natural hair characteristics, and desired style before any grafts are extracted.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Beard Transplant</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                The most technically demanding element of beard transplant is angle and direction control during implantation. Scalp hair grows at an angle of approximately 45 to 60 degrees from the skin surface. Beard hair grows at a much shallower angle, almost parallel to the skin, with specific directional patterns that vary across the face: downward on the cheeks, forward at the chin, and outward at the moustache. Implanting grafts at the wrong angle produces hair that stands away from the face rather than lying flat, which immediately reads as unnatural regardless of density.
              </p>
              <p>
                Dr. Jain maps the implantation angles across the planned beard area before surgery, matching the natural growth direction of the surrounding facial hair where it exists and replicating the anatomical pattern where it does not. DHI technique with a specialized implanter pen is preferred for beard work because it provides the most precise angle and depth control during implantation. Symmetry between the two sides of the face is assessed throughout the procedure. The result should be a beard that looks like it has always been there.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CandidateGridSection() {
  const cards = [
    {
      title: "Patchy or Uneven Beard",
      icon: Layers,
      copy: "Areas of absent growth within an otherwise present beard, creating uneven density across the cheeks, jawline, or moustache. Transplant fills these gaps with permanent hair matched to the density of surrounding areas.",
    },
    {
      title: "No Beard Due to Genetics",
      icon: User,
      copy: "Men with no beard growth due to genetic factors, where facial hair follicles are simply absent rather than dormant. Transplant introduces new follicles from the scalp donor area to areas where none exist.",
    },
    {
      title: "Beard Scars from Acne or Injury",
      icon: Zap,
      copy: "Scarring from severe acne, trauma, burns, or previous surgery creates areas of hair loss within the beard zone where follicles have been destroyed. Transplant into scarred areas is possible in selected cases depending on the quality of blood supply in the scar tissue.",
    },
    {
      title: "Desire for a Defined Beard Style",
      icon: Monitor,
      copy: "Patients who have some natural beard growth but want a more defined beard line, fuller coverage in specific zones, or a specific beard style that their natural growth pattern does not support. Transplant adds density and definition where the natural beard lacks it.",
    },
    {
      title: "Sparse Moustache or Goatee",
      icon: CircleDot,
      copy: "Isolated thinning or absence in the moustache or goatee area with adequate beard growth elsewhere. Transplant to specific zones addresses isolated deficiency without treating the entire beard area.",
    },
    {
      title: "Restoration After Hair Loss Conditions",
      icon: Sparkles,
      copy: "Alopecia areata and other hair loss conditions can cause patchy beard loss. When the condition is stable and inactive, transplant can restore the affected beard zones with permanent results.",
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20" data-testid="candidate-grid-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Who Is a Good Candidate for Beard Transplant?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed text-sm md:text-base">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AreasSection() {
  const cards = [
    {
      title: "Full Beard",
      icon: User,
      copy: "Complete beard coverage from the sideburns through the cheeks, jawline, and chin. Requires 2,000 to 3,000 grafts depending on the desired density and the area being covered. The most comprehensive beard transplant option.",
    },
    {
      title: "Jawline Definition",
      icon: Layers,
      copy: "Creating or sharpening the beard line along the jaw for a more defined lower face profile. Precise implantation at the beard border requires careful angle control to produce a natural, gradual edge rather than an artificial hard line.",
    },
    {
      title: "Moustache",
      icon: CircleDot,
      copy: "Filling sparse moustache areas or creating a moustache where growth is absent. Requires 500 to 800 grafts. The moustache zone requires particularly precise direction mapping as hair grows outward from the center philtrum in different directions on each side.",
    },
    {
      title: "Goatee",
      icon: Sparkles,
      copy: "Chin and lower lip area beard coverage for patients who want a defined goatee with or without surrounding cheek beard. Requires 800 to 1,500 grafts depending on the size and density desired.",
    },
    {
      title: "Sideburns",
      icon: Monitor,
      copy: "Extending or defining the sideburn area, filling sparse patches, or creating a natural transition between scalp hair and beard hair. Requires 200 to 500 grafts per side.",
    },
    {
      title: "Scar Correction in Beard Zone",
      icon: Zap,
      copy: "Restoring hair growth in beard areas affected by acne scarring, surgical scars, or traumatic injury. Scar tissue has reduced vascularity which affects graft survival. Dr. Jain assesses scar quality during consultation to determine whether transplant into the scarred area is appropriate.",
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="areas-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Beard Areas That Can Be Restored</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed text-sm md:text-base">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TechniquesSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="techniques-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Surgical Approach</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Beard Transplant Techniques</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div className="bg-white p-8 border border-[#E8E2D9]">
            <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">FUE (Follicular Unit Extraction)</h3>
            <p className="text-deep-navy/70 leading-relaxed">
              Individual follicles are extracted from the scalp donor area using a micro-punch of 0.7 to 1.0 millimeters. No linear incision is made and no stitches are required. The donor area heals with tiny marks not visible at normal hair lengths. Recipient channels are created in the beard zone and grafts are implanted. FUE is the standard technique for beard transplant and is appropriate for most patients and graft requirements.
            </p>
          </div>
          <div className="bg-white p-8 border border-[#E8E2D9]">
            <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">DHI (Direct Hair Implantation)</h3>
            <p className="text-deep-navy/70 leading-relaxed">
              Extracted follicles are implanted directly using a specialized implanter pen without pre-creating recipient channels. The pen controls depth, angle, and direction simultaneously during implantation, making it the preferred technique for beard work where precise angle control is the primary technical challenge. DHI also allows implantation into existing beard hair without shaving the recipient area, which is preferred by some patients who do not want to be without beard hair during the growth phase.
            </p>
          </div>
          <div className="bg-white p-8 border border-[#E8E2D9]">
            <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">Technique Selection</h3>
            <p className="text-deep-navy/70 leading-relaxed">
              Most beard transplant patients are treated with DHI for the implantation phase regardless of whether FUE or DHI is used for extraction, because the angle precision of the implanter pen is superior to manual channel creation with a blade for beard work. Dr. Jain discusses the appropriate technique for each patient during consultation based on the graft count, the areas being treated, and patient preference.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function HowPerformedSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-performed-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">How Beard Transplant Surgery is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              The procedure is performed under local anesthesia applied to both the donor area on the scalp and the recipient beard zone. Patients are awake and experience no pain during the procedure. The initial anesthesia injections produce brief discomfort that resolves within seconds. Total procedure time ranges from 4 to 8 hours depending on the number of grafts and areas being treated.
            </p>
            <p>
              After anesthesia, the donor area is prepared and grafts are extracted using FUE micro-punch technique. Extracted grafts are stored in a holding solution while the recipient beard zone is prepared. The implantation direction is mapped across the beard area before any grafts are placed. Grafts are implanted one by one using the DHI implanter pen at the planned angle and direction for each zone of the face.
            </p>
            <p>
              Post-procedure, a mild protective covering is applied to the donor area. The beard zone is left open. Written aftercare instructions are provided covering washing protocol, activity restrictions, and expected healing stages. Patients go home the same day.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ResultsTimelineSection() {
  const milestones = [
    {
      period: "Days 1 to 7",
      description:
        "Mild redness and small scabs at the implantation sites. Normal healing. The transplanted hairs are anchored but fragile during this period. Avoid touching or scratching the beard area.",
      icon: BedDouble,
    },
    {
      period: "Weeks 2 to 4",
      description:
        "Shock loss phase. The transplanted hairs shed while the follicles remain intact beneath the skin. This is expected and does not indicate graft failure. The follicles are alive and entering their resting phase before new growth.",
      icon: PersonStanding,
    },
    {
      period: "Months 3 to 4",
      description:
        "New beard growth begins. Fine hairs emerge from the transplanted follicles. Growth is uneven at this stage as different follicles emerge at different times.",
      icon: Monitor,
    },
    {
      period: "Months 6 to 9",
      description:
        "Full beard density achieved. The transplanted hair has reached its natural growth caliber. Shaving, trimming, and styling are fully normal. Final result is assessed at 9 to 12 months.",
      icon: Sparkles,
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="results-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">What to Expect</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-12 text-center">Beard Transplant Results Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={index} className="relative">
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={index} className="relative">
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function GraftRequirementsSection() {
  const rows = [
    { area: "Full Beard", detail: "2,000 to 3,000 grafts. Complete coverage of cheeks, jawline, and chin from sideburn to sideburn." },
    { area: "Goatee", detail: "800 to 1,500 grafts. Chin and lower lip zone beard coverage." },
    { area: "Moustache", detail: "500 to 800 grafts. Upper lip moustache zone." },
    { area: "Sideburns", detail: "200 to 500 grafts per side. Sideburn area extension or definition." },
    { area: "Scar Correction", detail: "200 to 800 grafts depending on scar size and location." },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="graft-requirements-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Am I a Candidate?</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">How Many Grafts Are Needed for Beard Transplant?</h2>
        <div className="max-w-3xl mx-auto border border-[#E8E2D9] bg-white overflow-hidden">
          {rows.map((row) => (
            <div
              key={row.area}
              className="grid grid-cols-1 md:grid-cols-[minmax(140px,180px)_1fr] gap-0 border-b border-[#E8E2D9] last:border-b-0"
            >
              <div className="p-4 md:p-5 bg-warm-white/80 font-serif text-premium-purple font-medium text-sm md:text-base">{row.area}</div>
              <div className="p-4 md:p-5 border-t md:border-t-0 md:border-l border-[#E8E2D9] text-deep-navy/70 text-sm leading-relaxed">{row.detail}</div>
            </div>
          ))}
        </div>
        <p className="max-w-3xl mx-auto mt-8 text-deep-navy/70 text-sm md:text-base leading-relaxed text-center md:text-left">
          These are approximate ranges. Exact graft requirements depend on the patient&apos;s desired density, the size of the recipient area, and the natural caliber of the donor hair. Dr. Jain calculates the specific requirement during consultation based on direct measurement and assessment.
        </p>
      </div>
    </section>
  );
}

function VsNonSurgicalSection() {
  const rows = [
    {
      label: "How It Works",
      transplant: "Introduces new permanent follicles to areas where none exist.",
      products: "Supports existing follicle health. Cannot create follicles where none are present.",
    },
    {
      label: "Result Type",
      transplant: "Permanent. New hair grows indefinitely.",
      products: "Temporary and inconsistent. Stops when product use stops.",
    },
    {
      label: "Effectiveness for Absent Beard",
      transplant: "Effective. Creates growth where follicles did not previously exist.",
      products: "Not effective. Cannot grow hair where no follicles are present.",
    },
    {
      label: "Timeline",
      transplant: "Visible results at 3 to 4 months. Full density at 9 to 12 months.",
      products: "Inconsistent. Most patients see no meaningful improvement in absent areas.",
    },
    {
      label: "Best For",
      transplant: "Patients with absent or patchy beard due to genetics, scarring, or follicle absence.",
      products: "Improving the condition and appearance of existing beard hair that is already growing.",
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="vs-non-surgical-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Understanding Your Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Beard Transplant vs. Beard Growth Products</h2>
        <div className="max-w-5xl mx-auto border border-[#E8E2D9] bg-white overflow-hidden">
          <div className="hidden md:grid md:grid-cols-3 gap-0 border-b border-[#E8E2D9] bg-premium-purple text-warm-white">
            <div className="p-4 font-serif text-sm" />
            <div className="p-4 font-serif text-sm border-l border-warm-white/20">Beard Transplant</div>
            <div className="p-4 font-serif text-sm border-l border-warm-white/20">Beard Oils and Supplements</div>
          </div>
          {rows.map((row) => (
            <div
              key={row.label}
              className="grid grid-cols-1 md:grid-cols-3 gap-0 border-b border-[#E8E2D9] last:border-b-0"
            >
              <div className="p-4 md:p-5 bg-warm-white/80 font-medium text-deep-navy text-sm md:text-base">{row.label}</div>
              <div className="p-4 md:p-5 border-t md:border-t-0 md:border-l border-[#E8E2D9] text-deep-navy/70 text-sm leading-relaxed">
                <span className="md:hidden font-serif text-premium-purple block mb-2">Beard Transplant</span>
                {row.transplant}
              </div>
              <div className="p-4 md:p-5 border-t md:border-t-0 md:border-l border-[#E8E2D9] text-deep-navy/70 text-sm leading-relaxed">
                <span className="md:hidden font-serif text-premium-purple block mb-2">Beard Oils and Supplements</span>
                {row.products}
              </div>
            </div>
          ))}
        </div>
        <p className="max-w-5xl mx-auto mt-8 text-deep-navy/70 text-sm md:text-base leading-relaxed text-center md:text-left">
          Non-surgical products are not alternatives to beard transplant for patients with absent or genuinely sparse follicles. They serve a maintenance and conditioning role for existing beard hair. Patients who have used products for months or years without meaningful improvement in growth are appropriate candidates for consultation on beard transplant.
        </p>
      </div>
    </section>
  );
}

const PatientResultsSection = () => <PatientTestimonials procedureName="Hair" />;

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {BEARD_TRANSPLANT_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`beard-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Beard Transplant Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses your beard growth pattern, scarring, donor density, and aesthetic goals to determine the graft requirement, appropriate technique, and the beard design that suits your facial structure. The implantation angle and direction plan are discussed before any procedure is proposed. You leave with a clear understanding of what is achievable and what the process involves.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button asChild className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
            asChild
            variant="outline"
            className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          >
            <a href={TEL_URL}>
              <Phone className="w-5 h-5 mr-2" />
              Call {PHONE_DISPLAY}
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Hair Transplant",
      description: "Scalp hair restoration for patients addressing both beard and scalp hair loss together.",
      href: HAIR_TRANSPLANT_PATH,
    },
    {
      name: "Gynecomastia Surgery",
      description: "For male patients combining chest correction with beard restoration in their aesthetic plan.",
      href: "/gynecomastia-surgery-mumbai",
    },
    {
      name: "Rhinoplasty",
      description: "Facial balance and proportion alongside beard definition for complete facial aesthetic improvement.",
      href: "/rhinoplasty-in-mumbai",
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function BeardTransplantPage() {
  return (
    <>
      <SEO
        title="Beard Transplant | FUE & DHI by Dr. Vicky Jain"
        description="Patchy beard, sparse growth, or beard scars from genetics or injury. Dr. Vicky Jain performs permanent beard transplant using FUE and DHI. Book today."
        canonical="/beard-transplant-mumbai"
      />
      <BeardTransplantJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <CandidateGridSection />
      <AreasSection />
      <TechniquesSection />
      <HowPerformedSection />
      <ResultsTimelineSection />
      <GraftRequirementsSection />
      <VsNonSurgicalSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
